// Shared helper functions used across multiple modules
// Extracted from portal.jsx — Phase 3 Batch 4.
// TypeScript source — helpers.js is a thin re-export stub.

import { deriveOverallStatus } from "../services/approvals";
import type { ChildRow } from "../types/database.js";
import { portalBaseUrl } from "./appUrl";
import { businessDay } from "./businessDate";
import { downloadCsv } from "./csv";
import { APPROVAL_STATUSES } from "./enums";
import { calcUniItems } from "./formConstants";

// A registered delivery address (subset of delivery_addresses used for matching).
interface DeliveryAddrLike {
  id?: string;
  street?: string | null;
  street2?: string | null;
  suburb?: string | null;
  // Suburb/City alignment (db-87): the single displayed locality. Prefer it over
  // the legacy `suburb`; fall back to `suburb` for rows not yet migrated.
  suburb_city?: string | null;
  state?: string | null;
  postcode?: string | null;
}

// Frontend-mapped request (camelCase) — accepts snake_case fallbacks too since
// some call sites pass a raw row. Only the fields these helpers read are listed.
interface RequestLike {
  refNo?: string | number | null;
  ref_no?: string | null;
  shelterName?: string | null;
  shelter_name?: string | null;
  state?: string | null;
  status?: string | null;
  date?: string | null;
  reportDay?: string | null;
  deliveryAddress?: string | null;
  delivery_address?: string | null;
  children?: ChildRow[];
}

// Money display with thousands separators + 2dp, e.g. money(1104) → "$1,104.00".
// Single source for currency formatting — prefer this over `$${n.toFixed(2)}`.
export function money(n: number | string | null | undefined): string {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Portal display: 2026-00001 (strips legacy SO- prefix)
export const formatRef = (refNo: string | null | undefined): string | null | undefined => {
  if (!refNo) return refNo;
  if (refNo.startsWith("SO-")) return refNo.slice(3);
  return refNo;
};

// Unleashed export: SO-2026-00001
export const formatRefForUnleashed = (
  refNo: string | null | undefined
): string | null | undefined => {
  if (!refNo) return refNo;
  if (refNo.startsWith("SO-") || refNo.startsWith("DRAFT")) return refNo;
  return `SO-${refNo}`;
};

// Parse YYYY-MM-DD as local date — avoids UTC timezone shift
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtDateLocal(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const s = String(dateStr);
  const yr = parseInt(s.slice(0, 4), 10);
  const mo = parseInt(s.slice(5, 7), 10);
  const dy = parseInt(s.slice(8, 10), 10);
  if (!yr || !mo || !dy) return s;
  return `${dy} ${MONTHS[mo - 1]} ${yr}`;
}

// Format an ISO timestamp (TIMESTAMPTZ) as "29 Apr 26, 2:30pm" — date + time.
// Falls back to fmtDateLocal if the input is just a YYYY-MM-DD date.
export function fmtDateTimeLocal(ts: string | null | undefined): string {
  if (!ts) return "";
  const s = String(ts);
  // Pure date with no time component
  if (s.length <= 10 || (s.indexOf("T") === -1 && s.indexOf(" ") === -1)) {
    return fmtDateLocal(s);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dy = d.getDate();
  const mo = MONTHS[d.getMonth()];
  const yr = String(d.getFullYear()).slice(2);
  const hr = d.getHours();
  const min = d.getMinutes();
  const ampm = hr >= 12 ? "pm" : "am";
  const hr12 = hr % 12 || 12;
  const minStr = min < 10 ? `0${min}` : String(min);
  return `${dy} ${mo} ${yr}, ${hr12}:${minStr}${ampm}`;
}

// Phase 3b — when a request is saved, the UI gives us a delivery address as a
// plain string. We need to decide whether it matches one of the shelter's
// registered delivery_addresses rows (-> save as delivery_address_id) or is
// a one-off (-> save as delivery_address_override).
// Matching format must match the fmtAddr helper in DeliverySelect:
//   [street, street2, suburb_city||suburb, state, postcode].filter(Boolean).join(", ")
export function resolveDeliveryForSave(
  text: string | null | undefined,
  deliveryAddresses: DeliveryAddrLike[] | null | undefined
) {
  if (!text) return { id: null, override: null };
  const list = deliveryAddresses || [];
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (!a) continue;
    const formatted = [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
      .filter(Boolean)
      .join(", ");
    if (formatted === text) return { id: a.id, override: null };
  }
  return { id: null, override: text };
}

export function buildRequestSummary(req: RequestLike): string {
  const lines = [];
  lines.push(`Reference: ${req.refNo || req.ref_no || ""}`);
  lines.push(`DFV Service: ${req.shelterName || req.shelter_name || ""}`);
  lines.push(`State: ${req.state || ""}`);
  lines.push(`Status: ${req.status || ""}`);
  lines.push(`Date: ${req.date || ""}`);
  lines.push(`Delivery Address: ${req.deliveryAddress || req.delivery_address || ""}`);
  // Canonical portal URL (VITE_PORTAL_URL / current origin) — never hardcode the
  // Vercel alias, so this survives retiring zephyr-portal-liard.vercel.app.
  lines.push(`View request: ${portalBaseUrl()}`);
  return lines.join("\n");
}

// Per-child money breakdown — the single source for "what does one child cost".
//   fees     = Σ fee amounts (school fees, camp, stationery, …)
//   uniforms = calcUniItems (includes named custom rows)
//   shoes    = jogger + leather (qty × cost)
//   laptops  = the device's estimatedCost, used as-is
// total = the four summed. calcRequestTotals is the request-level sum of this
// over every child, so the two can never drift.
//
// NOTE — two related per-child sums deliberately do NOT route through here
// because they compute a DIFFERENT number:
//   • RequestWizard / RequestForm (draft authoring) fall back to a standard cost
//     — deviceCost(standardCosts, deviceType) — when estimatedCost is still blank;
//   • ResponderChildView.childTotals splits the same amounts by approval status.
export function calcChildTotal(child: ChildRow | null | undefined) {
  if (!child) return { fees: 0, uniforms: 0, shoes: 0, laptops: 0, total: 0 };
  let fees = 0;
  (child.fees || []).forEach(function (f) {
    fees += parseFloat(f.feeAmount as string) || 0;
  });
  const uniforms = child.uniforms ? calcUniItems([child.uniforms]) : 0;
  const shoes = child.shoes
    ? (child.shoes.jQty || 0) * (parseFloat(child.shoes.jCost as string) || 0) +
      (child.shoes.lQty || 0) * (parseFloat(child.shoes.lCost as string) || 0)
    : 0;
  const laptops = child.laptop ? parseFloat(child.laptop.estimatedCost as string) || 0 : 0;
  return { fees, uniforms, shoes, laptops, total: fees + uniforms + shoes + laptops };
}

export function calcRequestTotals(r: RequestLike) {
  let nFees = 0,
    nUniforms = 0,
    nShoes = 0,
    nLaptops = 0;
  (r.children || []).forEach(function (child) {
    const t = calcChildTotal(child);
    nFees += t.fees;
    nUniforms += t.uniforms;
    nShoes += t.shoes;
    nLaptops += t.laptops;
  });
  return {
    fees: nFees,
    uniforms: nUniforms,
    shoes: nShoes,
    laptops: nLaptops,
    total: nFees + nUniforms + nShoes + nLaptops,
  };
}

// Which item decisions count, per the CSV's Approved / Declined / Both filter.
// "approved" (the default) counts approved items only.
export function decisionMatch(status: string | null | undefined, decision?: string): boolean {
  if (decision === "declined") return status === APPROVAL_STATUSES.DECLINED;
  if (decision === "both")
    return status === APPROVAL_STATUSES.APPROVED || status === APPROVAL_STATUSES.DECLINED;
  return status === APPROVAL_STATUSES.APPROVED;
}

// REPORTING ONLY: which fee types count as "Stationery" in the Reports CSV, the
// Statistics screen, the Statistics Summary PDF and Standard Report 2 (David, 12 Oct
// 2026). The warehouse pack ("Stationery") plus the school-billed "Stationery &
// Textbooks" and QLD "Textbooks". Every other fee type is "Fees". Do NOT use this for
// operational logic (picker tasks, sales orders, approval holds) — there "Stationery"
// means the physical pack only.
const STATIONERY_FEE_TYPES = ["Stationery", "Stationery & Textbooks", "Textbooks"];
export function isStationeryFee(feeType: string | null | undefined): boolean {
  return STATIONERY_FEE_TYPES.includes(feeType || "");
}

// Per-request totals for the items matching the decision filter: category $ (fees
// EXCLUDE stationery, which has its own figure — see isStationeryFee), the total, and
// the number of children with at least one matching item (`hasApproved` = that count
// > 0). The single source of decision math — used by the Statistics dashboard + PDF
// (called with "approved") and the decision-aware CSV export.
export function requestDecisionTotals(r: RequestLike, decision?: string) {
  let fees = 0;
  let stationery = 0;
  let uniforms = 0;
  let shoes = 0;
  let laptops = 0;
  let children = 0;
  (r.children || []).forEach(function (c) {
    let any = false;
    (c.fees || []).forEach(function (f) {
      if (decisionMatch(f.approvalStatus, decision)) {
        const amt = parseFloat(f.feeAmount as string) || 0;
        if (isStationeryFee(f.feeType)) stationery += amt;
        else fees += amt;
        any = true;
      }
    });
    if (c.uniforms && decisionMatch(c.uniforms.approvalStatus, decision)) {
      uniforms += calcUniItems([c.uniforms]);
      any = true;
    }
    if (c.shoes && decisionMatch(c.shoes.approvalStatus, decision)) {
      shoes +=
        (c.shoes.jQty || 0) * (parseFloat(c.shoes.jCost as string) || 0) +
        (c.shoes.lQty || 0) * (parseFloat(c.shoes.lCost as string) || 0);
      any = true;
    }
    if (c.laptop && decisionMatch(c.laptop.approvalStatus, decision)) {
      laptops += parseFloat(c.laptop.estimatedCost as string) || 0;
      any = true;
    }
    if (any) children++;
  });
  const total = fees + stationery + uniforms + shoes + laptops;
  return {
    fees,
    stationery,
    uniforms,
    shoes,
    laptops,
    total,
    children,
    hasApproved: children > 0,
  };
}

// The Reports "requests summary" table: header row + one row per request, as RAW
// cell values (not yet CSV-escaped). Pure, so it is unit-testable and can also feed
// an on-screen preview. Escaping happens once, in toCsv (utils/csv.ts) — the old
// hand-built join quoted only Status, so a DFV Service name containing a comma split
// into extra columns (production row 2026-03121, fixed 5 Oct 2026).
export function buildRequestCsvTable(
  rows: RequestLike[],
  decision?: string
): (string | number | null | undefined)[][] {
  const headers = [
    "Ref No",
    "DFV Service",
    "State",
    "Status",
    "Date",
    "Children",
    "Fees $",
    "Uniforms $",
    "Shoes $",
    "Devices $",
    "Stationery $",
    "Total $",
  ];
  return [
    headers,
    ...rows.map((r) => {
      // Decision-aware: the $ columns reflect only the items matching the
      // Approved / Declined / Both selection (default Approved). The $ columns add up
      // to Total $ (Fees $ excludes stationery — 12 Oct 2026). The only count is
      // Children (all children on the request, not decision-filtered).
      const t = requestDecisionTotals(r, decision);
      const children = (r.children || []).length;
      return [
        r.refNo,
        r.shelterName,
        r.state,
        r.status,
        r.reportDay || r.date,
        children,
        t.fees.toFixed(2),
        t.uniforms.toFixed(2),
        t.shoes.toFixed(2),
        t.laptops.toFixed(2),
        t.stationery.toFixed(2),
        t.total.toFixed(2),
      ];
    }),
  ];
}

export function exportToCSV(rows: RequestLike[], filename: string, decision?: string): void {
  // downloadCsv adds the BOM so Excel on Windows reads the file as UTF-8 (5 Oct 2026).
  downloadCsv(buildRequestCsvTable(rows, decision), filename);
}

// Build a School Locker "Shop by School" deep-link from the school name.
// School Locker URL pattern: https://theschoollocker.com.au/schools/<slug>
// where <slug> is the school name lowercased with hyphens between words and
// punctuation stripped. Strips trailing " - Suburb" or " (Suburb)" suffixes
// since the schools table stores name and suburb separately.
export function schoolLockerUrl(schoolName: string | null | undefined): string | null {
  if (!schoolName) return null;
  // Strip trailing " - Suburb" / " — Suburb" / " (Suburb)"
  const bare = String(schoolName)
    .split(/\s[—-]\s/)[0]
    .split(/\s\(/)[0];
  const slug = bare
    .toLowerCase()
    .replace(/[’']/g, "") // strip apostrophes
    .replace(/&/g, "and") // & -> and
    .replace(/[^a-z0-9]+/g, "-") // anything else -> hyphen
    .replace(/^-+|-+$/g, "") // trim leading/trailing hyphens
    .replace(/-{2,}/g, "-"); // collapse runs of hyphens
  if (!slug) return null;
  return `https://theschoollocker.com.au/schools/${slug}`;
}

// ── REQUEST ↔ DB MAPPING ──────────────────────────────────────────────────
// Extracted from portal.jsx so the load/save mapping can be unit-tested. These
// are the single translation point between the raw `requests_view` row
// (snake_case) and the in-memory app request (camelCase) the UI works with.

// Raw requests_view row — only the columns dbToReq reads. All optional/nullable
// since rows come straight from PostgREST.
interface RequestViewRow {
  // requests.id is a numeric (bigint) PK — never a string. Tightened from
  // `number | string` (Part C, 2026-06-15) so AppRequest.id is `number | undefined`.
  id?: number;
  shelter_id?: string | null;
  submitted_by?: string | null;
  ref_no?: string | null;
  shelter_ref?: string | null;
  shelter_name?: string | null;
  shelter_code?: string | null;
  state?: string | null;
  status?: string | null;
  // db-154 (stock-top-ups): 'fit_out' (default) or 'stock_top_up'. Threaded through
  // requests_view so lists can tell the two request kinds apart.
  request_type?: string | null;
  date?: string | null;
  created_at?: string | null;
  delivery_address_resolved?: string | null;
  delivery_address_id?: string | null;
  delivery_address_override?: string | null;
  contact_name?: string | null;
  non_registered_address?: boolean | null;
  shelter_address?: string | null;
  phone?: string | null;
  mobile?: string | null;
  caseworker_note?: string | null;
  notes?: unknown[] | null;
  children?: ChildRow[] | null;
  invoices?: unknown[] | null;
  docs?: unknown[] | null;
  updated_at?: string | null;
  updated_by?: string | null;
  updated_by_role?: string | null;
  approvals_confirmed_at?: string | null;
  cc_emails?: string[] | null;
  shelter_email?: string | null;
  shelter_has_unread_comment?: boolean | null;
  // The linked delivery address's zephyr_verified (db-100). null when the request
  // has no delivery_address_id (an old flat one-off) or on a list row that didn't
  // select it. false = a shelter-entered address awaiting responder verification.
  delivery_address_verified?: boolean | null;
  // The linked delivery address's banned_address (db-147). true = do-not-use →
  // BANNED token + hard approval block on dispatched items. null when no linked id.
  delivery_address_banned?: boolean | null;
}

// In-memory app request fields reqToDb reads back out (camelCase).
interface ReqToDbInput {
  refNo?: string | null;
  shelterUuid?: string | null;
  submittedBy?: string | null;
  shelterRef?: string | null;
  shelterName?: string | null;
  shelterCode?: string | null;
  state?: string | null;
  status?: string | null;
  date?: string | null;
  deliveryAddress?: string | null;
  phone?: string | null;
  mobile?: string | null;
  caseworkerNote?: string | null;
  contactName?: string | null;
  shelterAddress?: string | null;
  notes?: unknown[] | null;
  invoices?: unknown[] | null;
  docs?: unknown[] | null;
}

// The logged-in user, for the FK + delivery-address fields reqToDb needs.
interface SaveUser {
  id?: string | null;
  shelterUuid?: string | null;
  deliveryAddresses?: DeliveryAddrLike[] | null;
}

// Map a raw requests_view row → the in-memory app request (camelCase).
// IMPORTANT (the status contract): `status` is ALWAYS re-derived from the
// children's approval data via deriveOverallStatus, falling back to the stored
// status ONLY when there are no children to derive from. A stale stored status
// can therefore never leak through to the UI.
export function dbToReq(r: RequestViewRow) {
  return {
    id: r.id,
    // shelterUuid is the surrogate FK to shelters.id (set once at creation).
    // Captured here so reqToDb can preserve it through updates without needing
    // the editor's user object to supply it.
    shelterUuid: r.shelter_id,
    submittedBy: r.submitted_by,
    refNo: r.ref_no,
    shelterRef: r.shelter_ref,
    shelterName: r.shelter_name,
    shelterCode: r.shelter_code || "",
    state: r.state,
    // db-154 (stock-top-ups): the request kind. 'fit_out' (the standard request) or
    // 'stock_top_up'. Defaults to 'fit_out' for any row predating the column.
    requestType: r.request_type || "fit_out",
    // Status is re-derived from the children's approval data on load, with ONE
    // exception: a Draft is pre-submission — its items carry no approval
    // decisions yet, so deriving over it would wrongly surface a
    // draft-with-items as "Submitted" (deriveOverallStatus returns Submitted for
    // an all-Pending request). Drafts are identified by a DRAFT- ref (the
    // authoritative signal — it survives even if the stored status was clobbered)
    // or a stored "Draft" status, and always keep status "Draft".
    status:
      (r.ref_no != null && String(r.ref_no).startsWith("DRAFT")) || r.status === "Draft"
        ? "Draft"
        : deriveOverallStatus({ children: r.children || [] }) || r.status,
    date: r.date,
    createdAt: r.created_at || null,
    // The Brisbane BUSINESS day of creation (yyyy-mm-dd), derived from the UTC
    // created_at. This — not the legacy baked `date` string — is what reports,
    // date-range filters and the Submitted sort key off, so every viewer (anywhere
    // in the world) gets the same, DST-proof day. Falls back to `date` for any row
    // without a created_at. See docs/timezone-review-2026-09-03.md.
    reportDay: businessDay(r.created_at) || r.date || null,
    deliveryAddress: r.delivery_address_resolved || "",
    deliveryAddressId: r.delivery_address_id || null,
    deliveryAddressOverride: r.delivery_address_override || null,
    contactName: r.contact_name || "",
    nonRegisteredAddress: r.non_registered_address || false,
    shelterAddress: r.shelter_address || "",
    phone: r.phone,
    mobile: r.mobile ?? null,
    // The caseworker's one-time submission note (db-120). Its OWN field — it is
    // NOT part of the notes/comment thread. Read directly by CaseworkerNotesStrip.
    caseworkerNote: r.caseworker_note ?? null,
    notes: r.notes || [],
    children: r.children || null,
    invoices: r.invoices || [],
    docs: r.docs || [],
    updatedAt: r.updated_at || null,
    updatedBy: r.updated_by || null,
    updatedByRole: r.updated_by_role || null,
    approvalsConfirmedAt: r.approvals_confirmed_at || null,
    ccEmails: r.cc_emails || [],
    shelterEmail: r.shelter_email || "",
    // Option B unread-comment flag (db-77): true when staff have posted a comment
    // the DFV Service hasn't opened yet. Drives the reddy-orange Comments button.
    shelterHasUnreadComment: r.shelter_has_unread_comment || false,
    // db-100 (verified-address model): the linked delivery address's verified flag.
    // null = no linked address / not selected; false = pending Zephyr verification.
    deliveryAddressVerified: r.delivery_address_verified ?? null,
    // db-147 (promotion model): the linked delivery address's banned flag. true =
    // do-not-use → BANNED token + hard approval block on dispatched items.
    deliveryAddressBanned: r.delivery_address_banned ?? null,
  };
}

// Convert the in-memory app request (camelCase) → a DB row (snake_case).
// Children are NOT written here — they go via the save_request_children RPC.
// For UPDATEs, prefer the request's existing shelter_id/submitted_by (set at
// creation); fall back to the user's values only on first INSERT, so a
// staff/admin editor (who has no shelterUuid) can't clobber the shelter FK.
export function reqToDb(req: ReqToDbInput, user?: SaveUser | null) {
  const delivery = resolveDeliveryForSave(req.deliveryAddress, user?.deliveryAddresses);
  return {
    ref_no: req.refNo,
    shelter_id: req.shelterUuid || user?.shelterUuid || null,
    submitted_by: req.submittedBy || user?.id || null,
    shelter_ref: req.shelterRef,
    shelter_name: req.shelterName,
    // shelter_code is NO LONGER written to requests (identity Pass 2): the only
    // link to the shelter is shelter_id. The code shown on screens comes from the
    // shelters join in requests_view (db-85), like shelter_name — never a request
    // column. The requests.shelter_code column is dropped in db-86.
    state: req.state,
    status: req.status,
    date: req.date || new Date().toISOString().slice(0, 10),
    delivery_address_id: delivery.id,
    delivery_address_override: delivery.override,
    phone: req.phone,
    mobile: req.mobile || null,
    caseworker_note: req.caseworkerNote ?? null,
    contact_name: req.contactName || "",
    shelter_address: req.shelterAddress || "",
    notes: req.notes || [],
    invoices: req.invoices || [],
    docs: req.docs || [],
  };
}
